import type { CommentsNavigationState } from "@circle-react/components/CommentsCompass/types";
import { postsPath } from "@circle-react/helpers/urlHelpers";
import type { Post } from "@circle-react/types";
import { useNavigate } from "@tanstack/react-router";
import { pickBy } from "lodash";
import { useCallback } from "react";

import { useIsEmbed } from "@/react/hooks/useIsEmbed";
import type { PostModalParams } from "@/react/hooks/usePostModalParams";

interface NavigateToPostOptions extends CommentsNavigationState {
  hash?: string;
  hasNextPage?: boolean;
  isWithinProfileContext?: boolean;
  newPost?: Post;
  params?: Record<string, unknown>;
  pathname?: string;
  postModal?: PostModalParams;
  profileContextMember?: {
    id: number;
    public_uid: string;
  };
  replace?: boolean;
  shouldDisableNavigation?: boolean;
  startingImageIndex?: number;
}

const normalizeHash = (hash?: string) => hash?.replace(/^#/, "");

const POST_PAGE_PATH = "/c/$spaceSlug/$postSlug" as const;

export const useNavigateToPost = () => {
  const navigate = useNavigate();
  const isEmbed = useIsEmbed();

  const navigateToPost = useCallback(
    ({
      newPost,
      postModal,
      params,
      hasNextPage,
      hash,
      shouldFocusOnCommentsSection = false,
      shouldDisableNavigation = false,
      isWithinProfileContext,
      profileContextMember,
      startingImageIndex,
      replace = false,
    }: NavigateToPostOptions): void => {
      const normalizedHash = normalizeHash(hash);
      const spaceSlug = postModal?.spaceSlug ?? newPost?.space_slug;
      const postSlug = postModal?.postSlug ?? newPost?.slug;

      if (!spaceSlug || !postSlug) return;

      const state = pickBy(
        {
          postModal: { spaceSlug, postSlug },
          params,
          hasNextPage,
          shouldFocusOnCommentsSection,
          shouldDisableNavigation,
          isWithinProfileContext,
          profileContextMember,
          startingImageIndex,
        },
        value => value !== undefined,
      ) as Record<string, unknown>;

      if (isEmbed) {
        window.open(
          postsPath({ spaceSlug, slug: postSlug }),
          "_blank",
          "noreferrer",
        );
        return;
      }

      void navigate({
        to: ".",
        unsafeRelative: "path",
        search: true,
        state,
        replace,
        resetScroll: false,
        mask: {
          to: POST_PAGE_PATH,
          params: { spaceSlug, postSlug },
          hash: normalizedHash,
          unmaskOnReload: true,
        },
      });
    },
    [navigate, isEmbed],
  );

  return { navigateToPost };
};
